import JSZip from 'jszip';
import { getOnStorage } from '@/shared';
import { DataSetsProps } from '@/types';
import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import { normalizeDatasetType } from './_syncCommons';
import { formatFileSize } from '@/shared/commonFunctions';
import { useDbFunctions } from '../dbFunctions/useDBFunctions';
import { mergeDataValues } from '@/shared/datasetIncrementalMerge';
import { useEvents, getDateTimeMS } from '../use-events/useEvents';
import { _memTune, getDatasetKey, DatasetWithStoreMeta, updateProgress, PROGRESS_THROTTLE_MS, SPEED_UPDATE_INTERVAL_MS, progressLastCall } from './_syncCommons';

type DownloadEventProps = Record<string, { status: 0 | 1; arquivo?: string; dataFim?: string; dataInicio?: string }>;

type DatasetDownloadResult = {
  data: any;
  name: string;
  type: string;
  datasetKey: string;
};

type FetchDatasetsResponse = {
  byType: Record<string, { data: any }>;
  byDatasetKey: Record<string, DatasetDownloadResult>;
};

const MAX_CONCURRENT_DOWNLOADS = _memTune.maxConcurrentDownloads;

const runWithConcurrency = async <T>(tasks: (() => Promise<T>)[], max: number): Promise<PromiseSettledResult<T>[]> => {
  const results: PromiseSettledResult<T>[] = [];
  let index = 0;
  const runNext = async (): Promise<void> => {
    const i = index++;
    if (i >= tasks.length) return;
    try {
      const value = await tasks[i]();
      results[i] = { status: 'fulfilled', value };
    } catch (reason) {
      results[i] = { status: 'rejected', reason };
    }
    await runNext();
  };
  const workers = Array.from({ length: Math.min(max, tasks.length) }, () => runNext());
  await Promise.all(workers);
  return results;
};

const getProductCodeFromFilename = (filename: string) => {
  const onlyNumbers = filename.split('/').pop()?.replace(/\D+/g, '');
  const code = Number(onlyNumbers);
  return Number.isFinite(code) ? code : null;
};

const isZipContent = (resp: Response, url: string) => {
  const ct = resp.headers.get('content-type') || '';
  return ct.includes('zip') || url.toLowerCase().endsWith('.zip');
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const getDatasetProgressLabel = (dataset: DatasetWithStoreMeta) => {
  const base = dataset.DESC_ARQUIVO || normalizeDatasetType(dataset.TIPO_ARQUIVO);
  const loja = dataset.COD_LOJA != null && String(dataset.COD_LOJA).trim() !== '' ? String(dataset.COD_LOJA) : null;
  const segmento =
    dataset.COD_SEGMENTO != null && String(dataset.COD_SEGMENTO).trim() !== '' ? String(dataset.COD_SEGMENTO) : null;

  if (!loja && !segmento) return base;
  if (loja && segmento) return `${base} - Loja ${loja} / Segmento ${segmento}`;
  if (loja) return `${base} - Loja ${loja}`;
  return `${base} - Segmento ${segmento}`;
};

const isPlainObject = (value: any): value is Record<string, any> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const normalizeStockItem = (item: any) => {
  if (!isPlainObject(item)) return null;
  const codProdutoRaw = item.COD_PRODUTO ?? item.cod_produto;
  const qtdEstoqueRaw = item.QTD_ESTOQUE ?? item.qtd_estoque;
  if (codProdutoRaw == null || qtdEstoqueRaw == null) return null;

  const codProduto = Number(codProdutoRaw);
  const qtdEstoque = Number(qtdEstoqueRaw);
  if (!Number.isFinite(codProduto) || !Number.isFinite(qtdEstoque)) return null;

  const codLojaRaw = item.COD_LOJA ?? item.cod_loja;
  const codLoja = codLojaRaw == null || codLojaRaw === '' ? null : Number(codLojaRaw);

  return {
    ...item,
    COD_PRODUTO: codProduto,
    QTD_ESTOQUE: qtdEstoque,
    COD_LOJA: Number.isFinite(codLoja as number) ? (codLoja as number) : null,
  };
};

const normalizeStockData = (payload: any): any[] => {
  if (payload == null) return [];

  const stack: any[] = [payload];
  const seen = new Set<any>();
  const stockItems: any[] = [];

  while (stack.length) {
    const current = stack.pop();
    if (current == null || seen.has(current)) continue;
    if (typeof current === 'object') seen.add(current);

    if (Array.isArray(current)) {
      current.forEach((item) => {
        const normalizedItem = normalizeStockItem(item);
        if (normalizedItem) stockItems.push(normalizedItem);
        else if (item && typeof item === 'object') stack.push(item);
      });
      continue;
    }

    if (isPlainObject(current)) {
      Object.values(current).forEach((value) => {
        if (value != null && (Array.isArray(value) || isPlainObject(value))) stack.push(value);
      });
    }
  }

  return stockItems;
};

const normalizeParametroPrecoBaseData = (payload: any): any => {
  if (payload == null) return {};
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return payload;

  const candidates = [payload?.data, payload?.data?.data, payload].filter(isPlainObject) as Record<string, any>[];
  const dataRoot = candidates.find((candidate) => Object.keys(candidate).some((key) => Number.isFinite(Number(key))));

  return dataRoot || payload;
};

type UseDownloadFilesProps = {
  isSuccess: boolean;
  isOpenedModal: boolean;
  setDownloadSpeedBpsRef: React.MutableRefObject<(bps: number) => void>;
  setError: (error: boolean) => void;
  setIsSuccess: (isSuccess: boolean) => void;
  setFileDownload: (fileDownload: string) => void;
  setProgressPercent: (progressPercent: number) => void;
  setDownloadSpeedBps: (downloadSpeedBps: number) => void;
  setDownloadingFiles: (downloadingFiles: string[]) => void;
  setIsDownloadingFiles: Dispatch<SetStateAction<string[]>>;
  setDownloadingFileName: (downloadingFileName: string) => void;
  setStartDownloadingFile: (startDownloadingFile: string) => void;
  setFileDownloadProgress: (fileDownloadProgress: string) => void;
};

export const useFetchFiles = (_useStates: UseDownloadFilesProps) => {
  const displayLeaderRef = useRef<string | null>(null);

  const db = useDbFunctions();
  const { getOnDB } = db;
  const { registerEvent } = useEvents(db);
  
  const fetchDatasets = useCallback(
    async (list?: DataSetsProps[]) => {
      var totalSize = 0;
      var downloadedSize = 0;
      var speedLastTime = Date.now();
      var speedLastBytes = 0;
      _useStates.setDownloadSpeedBpsRef.current(0);
      const downloadEvents = {} as DownloadEventProps;

      const fetchAndSaveJson = async (
        url: string,
        name: string,
        desc: string,
        datasetKey: string,
        type: string,
        skipZipRedirect = false
      ): Promise<DatasetDownloadResult> => {
        if (!displayLeaderRef.current) displayLeaderRef.current = desc;
        if (displayLeaderRef.current === desc) {
          _useStates.setDownloadingFileName(desc);
          _useStates.setFileDownloadProgress(formatFileSize(0));
        }

        downloadEvents[name] = { ...(downloadEvents[name] || {}), arquivo: name, dataInicio: getDateTimeMS(), status: 1 };
        _useStates.setStartDownloadingFile(name);

        try {
          _useStates.setIsDownloadingFiles((prev) => [...prev, name]);
          const resp = await fetch(url, { cache: 'no-store' });

          if (!skipZipRedirect && isZipContent(resp, url)) {
            console.warn(`[${name}] conteúdo ZIP detectado, redirecionando para fetchAndSaveImages`);
            // Reutiliza a mesma Response — evita segundo fetch do mesmo ZIP (corpo ainda não foi lido).
            return fetchAndSaveImages(resp, name, desc, url, datasetKey, type);
          }

          if (resp.status === 403) {
            await registerEvent({
              propsNameUuid: name,
              offline: true,
              stEvento: 0,
              tipoEvento: 'DATASET_EXPIRADO',
              obsEvento: `Dataset expired: ${name}`,
            }).catch(() => {});
            throw new Error(`Failed to fetch ${name}: ${resp.statusText}`);
          } else if (resp.status >= 300 || resp.status < 200) {
            await registerEvent({
              propsNameUuid: name,
              offline: true,
              stEvento: 0,
              tipoEvento: 'DOWNLOAD',
              obsEvento: `Failed to fetch ${name}: ${resp.statusText}`,
            }).catch(() => {});
            throw new Error(`Failed to fetch ${name}: ${resp.statusText}`);
          }

          const contentLength = Number(resp.headers.get('content-length')) || 0;
          totalSize += contentLength;
          let totalArchive = contentLength;
          let downloadedArchive = 0;
          let lastFileDownloadUpdate = 0;

          if (displayLeaderRef.current === desc) {
            _useStates.setFileDownload(formatFileSize(totalArchive));
            _useStates.setFileDownloadProgress(formatFileSize(0));
            _useStates.setProgressPercent(0);
          }

          const reader = resp?.body?.getReader();
          if (!reader) throw new Error('Failed to get response body');
          const decoder = new TextDecoder('utf-8');
          const chunks: string[] = [];

          while (true) {
            const result = await reader.read();
            const { done, value } = result;
            if (done) break;
            const len = value?.byteLength ?? 0;
            downloadedSize += len || 1;
            downloadedArchive += len || 1;
            const now = Date.now();
            if (now - speedLastTime >= SPEED_UPDATE_INTERVAL_MS) {
              const elapsed = (now - speedLastTime) / 1000;
              if (elapsed > 0) {
                const bps = (downloadedSize - speedLastBytes) / elapsed;
                _useStates.setDownloadSpeedBpsRef.current(bps);
              }
              speedLastTime = now;
              speedLastBytes = downloadedSize;
            }
            if (!displayLeaderRef.current) {
              displayLeaderRef.current = desc;
              _useStates.setDownloadingFileName(desc);
            }
            if (now - lastFileDownloadUpdate >= PROGRESS_THROTTLE_MS && displayLeaderRef.current === desc) {
              lastFileDownloadUpdate = now;
              _useStates.setFileDownload(formatFileSize(totalArchive));
              _useStates.setFileDownloadProgress(formatFileSize(downloadedArchive));
            }
            updateProgress(downloadedArchive, totalArchive, name);
            if (displayLeaderRef.current === desc) updateProgress(downloadedArchive, totalArchive, 'progress');
            if (len) chunks.push(decoder.decode(value, { stream: true }));
          }
          progressLastCall[name] = 0;
          progressLastCall['progress'] = 0;
          if (displayLeaderRef.current === desc) {
            _useStates.setFileDownload(formatFileSize(totalArchive));
            _useStates.setFileDownloadProgress(formatFileSize(downloadedArchive));
            updateProgress(downloadedArchive, totalArchive, 'progress');
          }
          updateProgress(downloadedArchive, totalArchive, name);

          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;

          const json = JSON.parse(chunks.join(''));
          chunks.length = 0;
          let data = json?.catalogo ?? json?.preco ?? json;

          if (name === 'imagens') {
            data = typeof data === 'string' ? JSON.parse(data) : data;
            const out: Record<number, string> = {};
            if (Array.isArray(data)) {
              for (let j = 0; j < data.length; j++) {
                const item = data[j];
                out[item.COD_PRODUTO] = item.imagem?.replace(/^data:.*;base64,/i, '');
              }
            }
            data = out;
          }
          if (typeof data === 'string') data = JSON.parse(data);
          if (type === 'estoque') data = normalizeStockData(data);
          if (type === 'parametro_preco_base') data = normalizeParametroPrecoBaseData(data);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 1 };
          return { data, name, datasetKey, type };
        } catch (error) {
          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;
          console.error(`Error fetching ${name}:`, error);
          _useStates.setError(true);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 0 };
          throw error;
        }
      };

      const fetchAndSaveImages = async (
        resp: Response,
        name: string,
        desc: string,
        url: string,
        datasetKey: string,
        type: string
      ) => {
        if (!displayLeaderRef.current) displayLeaderRef.current = desc;
        if (displayLeaderRef.current === desc) {
          _useStates.setDownloadingFileName(desc);
          _useStates.setFileDownloadProgress(formatFileSize(0));
        }
        downloadEvents[name] = { ...(downloadEvents[name] || {}), arquivo: name, dataInicio: getDateTimeMS(), status: 1 };

        _useStates.setStartDownloadingFile(name);

        console.log('[images] processing zip from first fetch', { url });

        try {
          console.log('[images] response', { status: resp.status, ok: resp.ok, contentType: resp.headers.get('content-type') });

          if (!resp.ok) throw new Error(`Failed to fetch imagens zip: ${resp.statusText}`);
          if (!isZipContent(resp, url)) throw new Error('Conteúdo não é ZIP, fallback para JSON');

          const contentLength = Number(resp.headers.get('content-length')) || 0;
          totalSize += contentLength;
          let totalArchive = contentLength;
          let downloadedArchive = 0;
          let lastFileDownloadUpdate = 0;

          if (displayLeaderRef.current === desc) {
            _useStates.setFileDownload(formatFileSize(totalArchive));
            _useStates.setFileDownloadProgress(formatFileSize(0));
            _useStates.setProgressPercent(0);
          }

          const reader = resp.body?.getReader();
          if (!reader) throw new Error('Failed to get response body');
          const chunks: Uint8Array[] = [];

          while (true) {
            const result = await reader.read();
            const { done, value } = result;
            if (done) break;
            const len = value?.byteLength ?? 0;
            downloadedSize += len || 1;
            downloadedArchive += len || 1;
            const now = Date.now();
            if (now - speedLastTime >= SPEED_UPDATE_INTERVAL_MS) {
              const elapsed = (now - speedLastTime) / 1000;
              if (elapsed > 0) {
                const bps = (downloadedSize - speedLastBytes) / elapsed;
                _useStates.setDownloadSpeedBpsRef.current(bps);
              }
              speedLastTime = now;
              speedLastBytes = downloadedSize;
            }
            if (!displayLeaderRef.current) {
              displayLeaderRef.current = desc;
              _useStates.setDownloadingFileName(desc);
            }
            if (now - lastFileDownloadUpdate >= PROGRESS_THROTTLE_MS && displayLeaderRef.current === desc) {
              lastFileDownloadUpdate = now;
              _useStates.setFileDownload(formatFileSize(totalArchive));
              _useStates.setFileDownloadProgress(formatFileSize(downloadedArchive));
            }
            updateProgress(downloadedArchive, totalArchive, name);
            if (displayLeaderRef.current === desc) updateProgress(downloadedArchive, totalArchive, 'progress');
            if (value) chunks.push(value);
          }

          progressLastCall[name] = 0;
          progressLastCall['progress'] = 0;
          if (displayLeaderRef.current === desc) {
            _useStates.setFileDownload(formatFileSize(totalArchive));
            _useStates.setFileDownloadProgress(formatFileSize(downloadedArchive));
            updateProgress(downloadedArchive, totalArchive, 'progress');
          }
          updateProgress(downloadedArchive, totalArchive, name);
          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;

          const blob = new Blob(chunks as BlobPart[]);
          console.log(`[${name}] zip size (bytes)`, blob.size);

          const zip = await JSZip.loadAsync(blob);
          const fileEntries = Object.entries(zip.files);
          console.log(`[${name}] entries in zip`, fileEntries.length);
          console.log(
            `[${name}] sample names`,
            fileEntries.slice(0, 10).map(([n]) => n)
          );

          let manifestMap: Record<number, string> | null = null;
          const manifestEntry = Object.values(zip.files).find((f) => f.name.toLowerCase().endsWith('.json'));
          if (manifestEntry) {
            try {
              const manifestRaw = await manifestEntry.async('string');
              console.log(`[${name}] manifest raw (first 500 chars)`, manifestRaw.slice(0, 500));
              const manifest = JSON.parse(manifestRaw);
              manifestMap = Array.isArray(manifest)
                ? manifest.reduce((acc: Record<number, string>, item: any) => {
                    if (item?.COD_PRODUTO && item?.imagem) acc[item.COD_PRODUTO] = item.imagem;
                    return acc;
                  }, {})
                : Object.entries(manifest).reduce(
                    (acc, [key, val]) => {
                      const num = Number(key);
                      if (Number.isFinite(num)) acc[num] = String(val);
                      return acc;
                    },
                    {} as Record<number, string>
                  );
              console.log(`[${name}] manifest parsed keys`, Object.keys(manifestMap).length);
            } catch (err) {
              console.warn(`[${name}] manifest parse failed`, err);
              manifestMap = null;
            }
          } else {
            console.log(`[${name}] manifest not found`);
          }

          const imagesDict: Record<number, string> = {};
          let logged = 0;

          for (const [filename, file] of fileEntries) {
            if (file.dir || !/\.(webp|png|jpe?g)$/i.test(file.name)) continue;
            const code = getProductCodeFromFilename(manifestMap?.[Number(getProductCodeFromFilename(filename))] || filename);
            if (!code) continue;
            const imageBlob = await file.async('blob');
            const base64 = await blobToBase64(imageBlob);
            imagesDict[code] = base64.replace(/^data:.*;base64,/i, '');

            if (logged < 5) {
              console.log(`[${name}] saved sample`, {
                filename,
                code,
                blobSize: imageBlob.size,
                mime: imageBlob.type,
              });
              logged++;
            }
          }

          console.log(`[${name}] total images mapped`, Object.keys(imagesDict).length);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 1 };

          return { data: imagesDict, name, datasetKey, type };
        } catch (error) {
          console.warn(`Falha no ZIP de ${name}, tentando JSON. Motivo:`, error);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 0 };
          return fetchAndSaveJson(url, name, desc, datasetKey, type, true);
        }
      };

      const tasks = (list ?? []).map(
        (dataset, index) => () =>
          fetchAndSaveJson(
            dataset.URL_S3,
            `${normalizeDatasetType(dataset.TIPO_ARQUIVO)}-${(dataset as DatasetWithStoreMeta).COD_LOJA ?? 'all'}-${(dataset as DatasetWithStoreMeta).COD_SEGMENTO ?? 'all'}-${index}`,
            getDatasetProgressLabel(dataset as DatasetWithStoreMeta),
            getDatasetKey(dataset, index),
            normalizeDatasetType(dataset.TIPO_ARQUIVO)
          )
      );

      _useStates.setDownloadingFiles((list ?? []).map((item) => getDatasetProgressLabel(item as DatasetWithStoreMeta)));

      const responses = await runWithConcurrency(tasks, MAX_CONCURRENT_DOWNLOADS);
      const concatDownloadEvents = Object.values(downloadEvents)
        .map((evt) => `${evt.status} ${evt.arquivo} ${evt.dataInicio?.split('T')[1] || ''}-${evt.dataFim?.split('T')[1] || ''}`)
        .join('|');

      void registerEvent({
        tipoEvento: 'DOWNLOAD',
        obsEvento: concatDownloadEvents,
        stEvento: Object.values(downloadEvents).some((evt) => evt.status === 0) ? 0 : 1,
        codRca: Number(getOnStorage('cod_pessoa') || 0),
      }).catch(() => {});

      const byDatasetKey: Record<string, DatasetDownloadResult> = {};
      const groupedByType: Record<string, { data: any }> = {};
      const rejected: any[] = [];
      responses.forEach((item) => {
        if (item.status === 'fulfilled') {
          const value = item.value;
          byDatasetKey[value.datasetKey] = value;
          groupedByType[value.type] = {
            data: mergeDataValues(groupedByType[value.type]?.data, value.data),
          };
        }
        if (item.status === 'rejected') rejected.push(item.reason);
      });

      if (rejected.length > 0) {
        console.warn('Promises rejeitadas:', rejected);
      }

      _useStates.setDownloadSpeedBpsRef.current(0);
      if (!Object.keys(byDatasetKey).length) return undefined;
      return {
        byType: groupedByType,
        byDatasetKey,
      } as FetchDatasetsResponse;
    },
    [getOnDB]
  );

  return fetchDatasets;
};
